/**
 * Core Interactive Shader Format (ISF) types.
 *
 * Everything in `src/isf/` is deliberately free of `three`, `react` and store
 * imports. That is what lets the same code back the live renderer, the feed-card
 * preview renderer, the exporter and the importer without duplication, and be
 * unit tested without a DOM or a GL context.
 *
 * Spec: https://github.com/mrRay/ISF_Spec
 */

/** Every input type the ISF 2.0 spec defines. */
export type ISFInputType =
  | 'event'
  | 'bool'
  | 'long'
  | 'float'
  | 'point2D'
  | 'color'
  | 'image'
  | 'audio'
  | 'audioFFT';

/**
 * Input types that are fed by the host every frame rather than by the user.
 * These never receive a knob or a CC slot — a control the engine overwrites on
 * the next frame is meaningless, and it would shift every knob after it.
 */
export const HOST_DRIVEN_INPUT_TYPES: readonly ISFInputType[] = [
  'image',
  'audio',
  'audioFFT',
];

/**
 * Input NAMEs that the host drives even though they are declared as plain
 * scalars. They stay real ISF `float` inputs in the file — so VDMX and Resolume
 * still expose them as sliders the VJ can bind — but on our own platforms they
 * are auto-bound to the audio analyser and excluded from the CC pool.
 *
 * This set is part of the cross-platform contract: VIDOS must agree on it
 * exactly, which the conformance corpus asserts.
 */
export const HOST_DRIVEN_INPUT_NAMES: readonly string[] = [
  'bass',
  'mid',
  'treble',
  'level',
];

/** Uniforms ISF declares for you. An INPUT may never shadow one of these. */
export const ISF_RESERVED_UNIFORMS: readonly string[] = [
  'RENDERSIZE',
  'TIME',
  'TIMEDELTA',
  'DATE',
  'FRAMEINDEX',
  'PASSINDEX',
  'isf_FragNormCoord',
];

export interface ISFInput {
  NAME: string;
  TYPE: ISFInputType;
  LABEL?: string;
  DEFAULT?: number | number[] | boolean;
  MIN?: number | number[];
  MAX?: number | number[];
  IDENTITY?: number | number[];
  /** `long` only: the values behind each menu entry. */
  VALUES?: number[];
  /** `long` only: the menu entry labels, parallel to VALUES. */
  LABELS?: string[];
}

export interface ISFPass {
  TARGET?: string;
  PERSISTENT?: boolean | number | string;
  FLOAT?: boolean | number | string;
  /** Size equation, e.g. "$WIDTH / 2". Parsed by `expr.ts`, never eval'd. */
  WIDTH?: string | number;
  HEIGHT?: string | number;
}

export interface ISFImported {
  PATH: string;
}

export interface ISFDoc {
  ISFVSN?: string;
  VSN?: string;
  DESCRIPTION?: string;
  CREDIT?: string;
  CATEGORIES?: string[];
  INPUTS?: ISFInput[];
  PASSES?: ISFPass[];
  IMPORTED?: Record<string, ISFImported>;
}

/**
 * How a host files the shader. Resolume decides by folder and VIDOS by filename
 * prefix, so both are driven from this one classification.
 */
export type ISFClassification = 'generator' | 'filter' | 'transition';

export type DiagnosticSeverity = 'error' | 'warning' | 'info';

export interface ISFDiagnostic {
  /** 1-based line in WHOLE-FILE coordinates, so Monaco markers land correctly. */
  line: number;
  /** 1-based column, when known. */
  column?: number;
  severity: DiagnosticSeverity;
  /** Stable machine-readable code, e.g. `ISF_INPUT_SHADOWS_BUILTIN`. */
  code: string;
  message: string;
}
